import React from "react";
import classNames from "classnames";
import { GetServerSideProps, NextPage } from "next";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";

import { CATEGORY_NAMES } from "constants/dispensary";
import { Action, Category } from "constants/events";
import useDomain from "hooks/useDomain";
import { useEventTracker } from "hooks/useEventTracker";
import useShop, { isCategoryHub } from "hooks/useShop";

import FilterBar from "components/FilterBar";
import ShopDealsCarousel from "components/search-shop/ShopDealsCarousel";
import ShopMenuItemCarousels from "components/search-shop/ShopMenuItemCarousels";
import ShopResultsDescription from "components/search-shop/ShopResultsDescription";
import ShopSort from "components/search-shop/ShopSort";
import SearchAd from "components/SearchAd";
import RecentlyViewedMenuItems from "components/Shared/RecentlyViewedMenuItems";

const ShopSelection = dynamic(
  () => import("components/search-shop/ShopSelection"),
);
const PhenotypeCarousel = dynamic(() => import("components/PhenotypeCarousel"));
const ShopPromotedStrainsCarousel = dynamic(
  () => import("components/search-shop/ShopPromotedStrainsCarousel"),
);
const AllResultsButton = dynamic(() => import("components/AllResultsButton"));

const ShopPage: NextPage = () => {
  const baseUrl = useDomain();
  const { publishEvent } = useEventTracker();

  const {
    showEmptyState,
    dispensaries,
    totalCount,
    filters,
    selection,
    loading,
  } = useShop();

  const {
    asPath,
    query,
    query: {
      q,
      slug = [],
      shopCategory,
      shopName,
      product_category,
      strain_category,
      strain_name,
    },
  } = useRouter();

  const categoryHub = isCategoryHub(query);
  const isPromotedStrains = slug.length === 1 && slug[0] === "promoted-strains";
  const isPhenotype = slug.length === 1 && slug[0] === "phenotype";

  const adTargeting: { [key: string]: string } = {};
  let title = "Shop Leafly";
  if (categoryHub) {
    adTargeting.category = String(product_category);
    title = `Order ${CATEGORY_NAMES[String(product_category)]} online`;
  } else if (isPhenotype && strain_category) {
    adTargeting.phenotype = String(strain_category);
    title = `Order ${strain_category} online`;
  } else if (shopCategory === "brand" && shopName) {
    adTargeting.brand = String(shopName);
    title = `Order ${shopName} products online`;
  } else if (shopCategory === "strain" && shopName) {
    adTargeting.strain = String(shopName);
    title = `Order ${shopName} ${product_category || ""} online`;
  } else if (isPromotedStrains && strain_name) {
    adTargeting.promotedStrain = String(strain_name);
    title = `Order ${strain_name} on sale!`;
  } else if (q) {
    title = `Search results for "${q}"`;
  }

  return (
    <>
      <Head>
        <meta property="og:url" content={`${baseUrl}${asPath}`} key="og:url" />
        <title>Shop | Leafly</title>
        <meta name="robots" content="noindex,noarchive,nofollow" />
      </Head>
      <section className="container mt-xl mb-md">
        <h1 className="mb-md text-md md:text-lg">{title}</h1>

        {slug.length === 0 && (
          <ShopSelection
            filters={filters}
            selection={selection}
            className="mb-md"
          />
        )}
        {isPromotedStrains && <ShopPromotedStrainsCarousel />}
        {isPhenotype && <PhenotypeCarousel className="mt-lg mb-md" />}

        <FilterBar
          className="mb-md"
          filters={filters}
          selectionId={selection?.id}
          loading={loading}
          showEmptyState={showEmptyState}
        />
        <div className="md:flex justify-between">
          <ShopResultsDescription
            totalCount={totalCount}
            showEmptyState={showEmptyState}
          />
          <ShopSort showEmptyState={showEmptyState} />
        </div>
        {showEmptyState && <AllResultsButton className="my-xl" />}
      </section>

      <section className="bg-leafly-white py-xl">
        <SearchAd
          tag="availability"
          customTargeting={adTargeting}
          className="gam-rectangular-fixed-height"
        />
        <div className={classNames("container", { "mt-xl": !showEmptyState })}>
          <ShopMenuItemCarousels dispensaries={dispensaries} />
        </div>
      </section>

      <section className="container">
        <ShopDealsCarousel />
        <RecentlyViewedMenuItems
          className="my-section"
          onCardImpression={(menuItem) => {
            publishEvent({
              action: Action.impression,
              category: Category.recentlyViewedMenuItems,
              dispensaryId: menuItem.dispensaryId,
            });
          }}
        />
      </section>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const slugs = context.query.slug || [];
  const isPromotedStrains =
    slugs.length === 1 && slugs[0] === "promoted-strains";
  const isPhenotype = slugs.length === 1 && slugs[0] === "phenotype";

  // This file covers only /search/shop, /search/shop/phenotype, and /search/shop/promoted-strains
  if (slugs.length === 0 || isPromotedStrains || isPhenotype) {
    return {
      props: {},
    };
  } else {
    return {
      notFound: true,
    };
  }
};

export default ShopPage;
